import { WebSocket } from "ws";
import { ClientMessage, ServerProtocolMessage } from "@inline-chat/protocol/core";
import { AsyncChannel } from "../utils/async-channel.js";
import { TransportError } from "./transport.js";
export class WebSocketTransport {
    events = new AsyncChannel();
    url;
    log;
    state = "idle";
    connectionAttemptNo = 0;
    socket = null;
    reconnectionTimer = null;
    lastConnectStartedAt = null;
    lastConnectedAt = null;
    lastDisconnectedAt = null;
    lastMessageAt = null;
    lastCloseCode = null;
    lastCloseReason = null;
    lastErrorAt = null;
    lastErrorMessage = null;
    lastReconnectScheduledAt = null;
    lastReconnectDelayMs = null;
    lastReconnectCause = null;
    reconnectCount = 0;
    reconnectScheduling = false;
    constructor(options) {
        this.url = options.url;
        this.log = options.logger ?? {};
    }
    async start() {
        if (this.state !== "idle")
            return;
        await this.setConnecting();
        await this.openConnection();
    }
    async stop() {
        if (this.state === "idle")
            return;
        await this.setIdle();
        this.cleanUpPreviousConnection();
    }
    async send(message) {
        if (this.state !== "connected" || !this.socket || this.socket.readyState !== WebSocket.OPEN) {
            throw TransportError.notConnected();
        }
        this.socket.send(ClientMessage.toBinary(message));
    }
    async stopConnection() {
        this.cleanUpPreviousConnection();
    }
    async reconnect(options) {
        const skipDelay = options?.skipDelay ?? false;
        // A single socket failure often emits both `error` and `close`. Claim the
        // scheduling slot before awaiting so concurrent handlers cannot both queue
        // reconnect timers for the same disconnect cycle.
        if (this.state === "connecting" && (this.reconnectionTimer || this.reconnectScheduling)) {
            return;
        }
        this.reconnectScheduling = true;
        try {
            await this.setConnecting();
            if (this.state === "idle" || this.reconnectionTimer)
                return;
            this.cleanUpPreviousConnection();
            this.connectionAttemptNo = (this.connectionAttemptNo + 1) >>> 0;
            const delaySeconds = this.getReconnectionDelaySeconds(this.connectionAttemptNo);
            const delayMs = Math.round(delaySeconds * 1000);
            const cause = options?.cause?.trim() || "unspecified";
            this.lastReconnectScheduledAt = Date.now();
            this.lastReconnectDelayMs = delayMs;
            this.lastReconnectCause = cause;
            this.reconnectCount += 1;
            this.log.warn?.(`WebSocket reconnect scheduled (attempt=${this.connectionAttemptNo}, delayMs=${delayMs}, cause=${cause})`);
            this.reconnectionTimer = setTimeout(() => {
                this.reconnectionTimer = null;
                if (this.state === "idle" || this.state === "connected")
                    return;
                void this.openConnection();
            }, skipDelay ? 0 : delaySeconds * 1000);
        }
        finally {
            this.reconnectScheduling = false;
        }
    }
    cleanUpPreviousConnection() {
        if (this.reconnectionTimer) {
            clearTimeout(this.reconnectionTimer);
            this.reconnectionTimer = null;
        }
        if (this.socket) {
            const socket = this.socket;
            this.socket = null;
            socket.removeAllListeners();
            this.closeSocketSafely(socket);
        }
    }
    closeSocketSafely(socket) {
        try {
            if (socket.readyState === WebSocket.CONNECTING) {
                socket.terminate();
                return;
            }
            if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CLOSING) {
                socket.close();
            }
        }
        catch (error) {
            this.log.warn?.("WebSocket cleanup close failed", error);
            try {
                socket.terminate();
            }
            catch {
                // no-op
            }
        }
    }
    getReconnectionDelaySeconds(attemptNo) {
        // Exponential-ish backoff with jitter.
        // Cap quickly so callers don't stall for too long.
        const base = Math.min(8.0, 0.2 + Math.pow(attemptNo, 1.5) * 0.4);
        const jitter = attemptNo >= 8 ? Math.random() * 4.0 : 0;
        return base + jitter;
    }
    async openConnection() {
        if (this.state === "idle")
            return;
        this.lastConnectStartedAt = Date.now();
        const socket = new WebSocket(this.url);
        this.socket = socket;
        socket.on("open", () => {
            void this.connectionDidOpen(socket);
        });
        socket.on("message", (data) => {
            void this.handleMessage(socket, data);
        });
        socket.on("close", (code, reason) => {
            void this.handleClose(socket, code, reason);
        });
        socket.on("error", (error) => {
            void this.handleError(socket, error);
        });
    }
    async connectionDidOpen(socket) {
        if (this.socket !== socket)
            return;
        if (this.state !== "connecting")
            return;
        this.connectionAttemptNo = 0;
        this.state = "connected";
        this.lastConnectedAt = Date.now();
        this.lastDisconnectedAt = null;
        await this.events.send({ type: "connected" });
    }
    async handleMessage(socket, data) {
        if (this.socket !== socket)
            return;
        try {
            const payload = this.coerceBinary(data);
            const message = ServerProtocolMessage.fromBinary(payload);
            this.lastMessageAt = Date.now();
            await this.events.send({ type: "message", message });
        }
        catch (error) {
            this.log.error?.("Failed to decode message", error);
        }
    }
    coerceBinary(data) {
        if (data instanceof ArrayBuffer)
            return new Uint8Array(data);
        // Covers Buffer and other typed-array views.
        if (ArrayBuffer.isView(data))
            return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
        if (Array.isArray(data))
            return new Uint8Array(Buffer.concat(data));
        throw new Error("Unsupported WebSocket message payload");
    }
    async handleClose(socket, code, reason) {
        if (this.socket !== socket)
            return;
        if (this.state === "idle")
            return;
        const reasonText = stringifyCloseReason(reason);
        this.lastDisconnectedAt = Date.now();
        this.lastCloseCode = code;
        this.lastCloseReason = reasonText;
        const connectedForMs = this.lastConnectedAt != null ? Math.max(0, this.lastDisconnectedAt - this.lastConnectedAt) : null;
        this.log.warn?.(`WebSocket closed (code=${code}, reason=${reasonText || "none"}${connectedForMs != null ? `, connectedForMs=${connectedForMs}` : ""})`);
        await this.reconnect({ cause: `socket-close:${code}${reasonText ? `:${reasonText}` : ""}` });
    }
    async handleError(socket, error) {
        if (this.socket !== socket)
            return;
        if (this.state === "idle")
            return;
        const summary = summarizeError(error);
        this.lastErrorAt = Date.now();
        this.lastErrorMessage = summary;
        this.log.error?.(`WebSocket error: ${summary}`);
        await this.reconnect({ cause: `socket-error:${summary}` });
    }
    async setIdle() {
        if (this.state === "idle")
            return;
        this.state = "idle";
        await this.events.send({ type: "stopping" });
    }
    async setConnecting() {
        if (this.state === "connecting")
            return;
        this.state = "connecting";
        await this.events.send({ type: "connecting" });
    }
    getDiagnostics() {
        return {
            kind: "websocket",
            url: redactUrlForDiagnostics(this.url),
            state: this.state,
            connectionAttemptNo: this.connectionAttemptNo,
            reconnectCount: this.reconnectCount,
            lastConnectStartedAt: this.lastConnectStartedAt,
            lastConnectedAt: this.lastConnectedAt,
            lastDisconnectedAt: this.lastDisconnectedAt,
            lastMessageAt: this.lastMessageAt,
            lastCloseCode: this.lastCloseCode,
            lastCloseReason: this.lastCloseReason,
            lastErrorAt: this.lastErrorAt,
            lastErrorMessage: this.lastErrorMessage,
            lastReconnectScheduledAt: this.lastReconnectScheduledAt,
            lastReconnectDelayMs: this.lastReconnectDelayMs,
            lastReconnectCause: this.lastReconnectCause,
            socketReadyState: this.socket?.readyState ?? null,
        };
    }
}
function stringifyCloseReason(reason) {
    const text = reason.toString("utf8").trim();
    return text;
}
function summarizeError(error) {
    if (error instanceof Error) {
        const code = typeof error.code === "string" ? error.code : null;
        return code ? `${error.name}: ${error.message} (code=${code})` : `${error.name}: ${error.message}`;
    }
    return String(error);
}
function redactUrlForDiagnostics(raw) {
    try {
        const url = new URL(raw);
        return `${url.protocol}//${url.host}${url.pathname}`;
    }
    catch {
        return raw;
    }
}
